// Chart arithmetic, deliberately free of any DOM so it can be tested directly.

/** A linear mapping from `domain` onto `range`. A zero-width domain maps
 *  everything to the middle of the range rather than dividing by zero — which
 *  happens whenever an item held one price all week. */
export function linear(
  domain: [number, number],
  range: [number, number],
): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (span === 0) {
    const middle = (r0 + r1) / 2;
    return () => middle;
  }
  return (value) => r0 + ((value - d0) / span) * (r1 - r0);
}

/** Round axis values that *bracket* [min, max]: the first tick sits at or below
 *  min and the last at or above max. That bracketing is the whole contract —
 *  the chart scales to `ticks[0]`..`ticks[last]`, so a tick range narrower than
 *  the data silently renders the highest price outside the viewBox. Not a
 *  general-purpose nice-number algorithm; just enough to keep a price axis
 *  readable. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [min];
  if (min > max) [min, max] = [max, min];
  if (min === max) return [min];
  const n = Math.max(1, Math.floor(count));
  const rawStep = (max - min) / n;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 5, 10]
    .map((m) => m * magnitude)
    .find((candidate) => candidate >= rawStep) ?? magnitude * 10;
  // Round down to start, round UP to end. The obvious `value <= max + step / 2`
  // stopping condition is not equivalent and undershoots max about half the
  // time — niceTicks(7, 16, 4) gives [5, 10, 15] under it, dropping 16 off the
  // top of the axis. Indexing off `start` rather than accumulating `value +=
  // step` also keeps floating-point error from compounding along a long axis.
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const steps = Math.round((end - start) / step);
  return Array.from({ length: steps + 1 }, (_, i) => start + i * step);
}

/** The whole-day number a `YYYY-MM-DD` bucket label names, counted from the
 *  epoch — so two labels are consecutive days exactly when their indices differ
 *  by one. `null` for a label that is not a date, which is what `bucket=scan`
 *  returns.
 *
 *  Read off the label rather than measured from the timestamps, and that is the
 *  whole reason this function exists. A HistoryPoint's `first_seen` is
 *  `MIN(observed_at)` *inside* its bucket, so two consecutive days scanned at
 *  09:00 and at 21:00 are 36 hours apart, while a day scanned at 23:59 and the
 *  day after next scanned at 00:01 are 24 hours and two minutes apart. The two
 *  ranges overlap, so no threshold on those timestamps can tell "the next day"
 *  from "a day nobody scanned". The label can, exactly.
 *
 *  This is not the join PriceHistory.tsx's header warns against. That hazard is
 *  formatting a *browser-local* time into the server's YYYY-MM-DD and matching
 *  the strings, where the viewer's zone and APP_TIMEZONE disagree about which
 *  day it is. Here both sides of every comparison are server-issued labels put
 *  through the same parse, so the viewer's zone cannot enter the arithmetic —
 *  `Date.UTC` rather than `new Date(text)` precisely so that it cannot. What
 *  the index means in absolute terms is never used; only the difference is. */
export function dayIndex(bucket: string): number | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucket);
  if (parts === null) return null;
  const at = Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return Number.isNaN(at) ? null : Math.round(at / 86_400_000);
}

/** Split a series into runs of consecutive points, so a line can be drawn with
 *  a break wherever the series skips.
 *
 *  `index` puts each point on the axis's own ruler — for a daily chart, the day
 *  number from `dayIndex`. A run ends between two points whose indices differ
 *  by more than `maxStep`.
 *
 *  This exists because one continuous polyline through every bucket draws a run
 *  of unscanned days as a long straight segment, which asserts prices between
 *  its ends that nobody observed. That is the same fabrication the ±5% area
 *  band was deleted for (see PriceHistory.tsx) — a line is simply a quieter
 *  place to do it.
 *
 *  A `null` index never breaks a run. An unknown spacing is not evidence of a
 *  gap, and the alternative — reading "I cannot tell" as "there is a hole" —
 *  shatters the whole chart into single points the moment a caller buckets by
 *  scan instead of by day, which is a supported argument to this endpoint. */
export function segments<T>(
  points: T[],
  index: (point: T) => number | null,
  maxStep = 1,
): T[][] {
  if (points.length === 0) return [];
  const runs: T[][] = [[points[0]]];
  for (let i = 1; i < points.length; i += 1) {
    const before = index(points[i - 1]);
    const here = index(points[i]);
    const broken = before !== null && here !== null && here - before > maxStep;
    if (broken) runs.push([points[i]]);
    else runs[runs.length - 1].push(points[i]);
  }
  return runs;
}

/** Push two label positions apart until they are `gap` apart, moving each the
 *  same distance so the pair stays centred on where it was.
 *
 *  For the two direct labels at the end of a price chart. They sit at their own
 *  series' y, and a market whose bid and ask have converged — which is the
 *  interesting case, not the exceptional one — puts both strings on the same
 *  baseline, overprinted into an unreadable smudge. A ground-coloured halo does
 *  not help here: the collision is text against text, and whichever draws
 *  second simply knocks the first one out.
 *
 *  Order is preserved, so the label of the higher price stays above the label
 *  of the lower one and neither ever crosses the other's line. Identical
 *  positions are split symmetrically rather than left to a comparison that
 *  would have to invent an order. */
export function separate(a: number, b: number, gap: number): [number, number] {
  const delta = b - a;
  if (Math.abs(delta) >= gap) return [a, b];
  const push = (gap - Math.abs(delta)) / 2;
  return delta >= 0 ? [a - push, b + push] : [a + push, b - push];
}

export function path(points: [number, number][]): string {
  if (points.length === 0) return "";
  return points
    .map(([x, y], index) => `${index === 0 ? "M" : "L"}${round(x)} ${round(y)}`)
    .join(" ");
}

/** A closed area between two edges, the lower one walked backwards so the
 *  outline does not cross itself.
 *
 *  The `.replace("M", "L")` below relies on `path()` emitting exactly one "M",
 *  at index 0 — guaranteed by the emptiness guard here, and safe because a
 *  number never stringifies to contain an "M". If `path()` ever emits a
 *  different leading command, this breaks silently. */
export function band(
  upper: [number, number][],
  lower: [number, number][],
): string {
  if (upper.length === 0 || lower.length === 0) return "";
  const back = [...lower].reverse();
  return `${path(upper)} ${path(back).replace("M", "L")} Z`;
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
